import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import Seal from '../components/Seal';
import { fmtDate, fmtDateTime, fmtMoney, statusTag, urgencyDot, toLocalInput } from '../lib/format';
import SwipeToDelete from '../components/SwipeToDelete';
import ClientProfile from '../components/ClientProfile';
import { ledgerTotals, credentialTag, expiryPhrase } from '../lib/ledger';
import LedgerCard from '../components/LedgerCard';
import MessageButton from '../components/MessageButton';
import InstallCard from '../components/InstallCard';
import AdminProjectDetail from '../components/AdminProjectDetail';
import CardMenu, { MenuIcons } from '../components/CardMenu';
import MediaStrip from '../components/MediaStrip';
import DocumentList from '../components/DocumentList';
import { vendorMenuItems } from '../lib/cardActions';
import { TAXONOMY } from './VendorDashboard';
import Marketing from '../components/Marketing';

// Groups vendors into board columns by the trades they picked in their
// application. A vendor who does three trades appears in all three columns —
// that's how the old Trello board worked. Anyone with no trade on file lands in
// a catch-all column so they never disappear.
const NO_TRADE = 'No trade on file';
function vendorColumns(vendors) {
  const cols = new Map();
  const push = (trade, v) => {
    if (!cols.has(trade)) cols.set(trade, []);
    cols.get(trade).push(v);
  };
  vendors.forEach(v => {
    const trades = [...(v.profile?.expertise || [])];
    if (v.profile?.expertiseOther) trades.push(v.profile.expertiseOther);
    if (trades.length === 0) push(NO_TRADE, v);
    else trades.forEach(tr => push(tr, v));
  });
  // Watched first (that's what the eye is for), then whoever covers the most
  // trades — the one-stop techs are worth seeing before you assemble a crew.
  cols.forEach(list => list.sort((a, b) =>
    (b.watched ? 1 : 0) - (a.watched ? 1 : 0) || tradesOf(b).length - tradesOf(a).length
  ));
  // Busiest trades first; the catch-all always sits last.
  return [...cols.entries()]
    .sort((a, b) => (a[0] === NO_TRADE) - (b[0] === NO_TRADE) || b[1].length - a[1].length || a[0].localeCompare(b[0]));
}

// Traffic light for a vendor card. The server already works out whether their
// paperwork is current, expiring inside 90 days, or lapsed — this just turns
// that into the colour Julian reads at a glance:
//   green  = everything in date, good to send work to
//   yellow = a renewal is coming up, chase it before it becomes a problem
//   red    = expired or missing, can't be used until it's fixed
const VENDOR_LIGHTS = {
  valid:    { edge: 'var(--green)', tint: 'var(--green-light)', text: 'var(--green)', label: 'Good to use — paperwork current' },
  expiring: { edge: 'var(--amber)', tint: 'var(--amber-light)', text: 'var(--amber)', label: 'Renewal coming up' },
  blocked:  { edge: 'var(--red)',   tint: 'var(--red-light)',   text: 'var(--red)',   label: "Can't use — paperwork not current" },
};
const lightFor = (v) => VENDOR_LIGHTS[v.compliance?.status] || VENDOR_LIGHTS.blocked;

// Column headings use the full trade name; the chips on a card can't — four of
// these are long enough to wrap a card three lines deep on a phone.
const SHORT_TRADE = {
  'Interior and Exterior Painting': 'Painting',
  'Demolition and Junk Removal': 'Demolition',
  'Remodeling & Construction': 'Remodeling',
  'General Repairs': 'Repairs',
};
const shortTrade = (t) => SHORT_TRADE[t] || t;

// Every trade a vendor said they can do, in the order they picked them.
const tradesOf = (v) => {
  const list = [...(v.profile?.expertise || [])];
  if (v.profile?.expertiseOther) list.push(v.profile.expertiseOther);
  return list;
};

// One vendor card on the board — name, phone, and the labels that matter at a
// glance. Tapping it opens that vendor's full profile (jobs, ledger, progress).
function VendorCard({ v, onOpen, menuItems, column, onDragStart, onDragEnd, dragging }) {
  const p = v.profile || {};
  const light = lightFor(v);
  const soonest = (v.compliance?.items || [])
    .filter(i => i.status === 'expiring')
    .sort((a, b) => a.daysLeft - b.daysLeft)[0];
  const trades = tradesOf(v);

  return (
    <div
      className={`vcard${dragging ? ' is-dragging' : ''}`}
      style={{ borderLeftColor: light.edge, background: light.tint }}
      onClick={() => onOpen(v)}
      // Mouse drag only — HTML5 drag events don't fire on touch, which is why
      // "Move card" also lives in the menu for phone use.
      draggable={Boolean(onDragStart)}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <CardMenu items={menuItems(v, column)} label={`Actions for ${v.businessName}`} />
      <div className="vcard-name">
        {v.watched && <span className="vcard-watch" title="Watching">{MenuIcons.eye}</span>}
        {v.businessName}
      </div>
      <div className="vcard-meta">{v.phone || v.userId?.phone || 'no phone on file'}</div>

      {/* Every trade this tech can do, not just the column you're standing in.
          This is the whole point of the board: you scan a trade column, and the
          card tells you the same person also covers electrical and drywall — so
          one tech takes the whole job instead of three showing up. The column
          you're in is filled; the rest are outlined. */}
      {trades.length > 0 && (
        <div className="vcard-trades">
          {trades.map(t => (
            <span key={t} className={`vtrade${t === column ? ' here' : ''}`}>{shortTrade(t)}</span>
          ))}
          {trades.length > 1 && (
            <span className="vtrade-count">covers {trades.length} trades</span>
          )}
        </div>
      )}

      <div className="vcard-labels">
        <span className="vcard-label" style={{ background: 'white', color: light.text }}>{light.label}</span>
        {soonest && (
          <span className="vcard-label" style={{ background: 'white', color: light.text }}>
            {soonest.label}: {expiryPhrase(soonest.daysLeft)}
          </span>
        )}
        {!p.completedAt && <span className="vcard-label" style={{ background: 'white', color: 'var(--muted)' }}>Application pending</span>}
        {p.hasCrew === 'Yes' && <span className="vcard-label" style={{ background: 'white', color: 'var(--teal)' }}>Has crew</span>}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const {
    currentScreen, goTo, logout, activeTab, setActiveTab, requests,
    setActiveRequest, setModal, setAssignVendorId, handleApproveInvoice,
    handleAdminApproveEstimate, openEstimate,
    openWorkOrder, isRaisingWorkOrder, setPrintDoc,
    setAppointmentInput, adminView, setAdminView, clients, vendors,
    setConfirmDelete,
    ledger, ledgerVendorFilter, setLedgerVendorFilter,
    openLedgerForm, handleDeleteLedgerEntry, handleExportLedger,
    openRecordPayment, handleDeletePayment, openProgress, openCredential,
    activeVendor, setActiveVendor, vendorLayout, setVendorLayout,
    projects, activeProject, setActiveProject, estimates,
    openProjectForm, handleDeleteProject, handleApproveEstimateToProject,
    handleToggleWatch, handleMoveVendorTrade, showToast, openVendorEdit,
    smsStatus, refreshSmsStatus, isLoadingSms,
    adminAccounts, loadAdminAccounts, adminAccountDetail, openAdminAccount,
    openAccountForm, openAccountMemberForm, handleRemoveAccountMember, openAdminPropertyForm,
    adminPropertyId, setAdminPropertyId, openPropertyJobForm, openAdminInvoice,
    addAdminPropertyDocuments, removeAdminPropertyDocument, addJobDocuments,
    pushOn, pushBusy, togglePush, sendTestPush, pushBlockedReason, isInstalled,
    openAddClientForm, openClientForm, openVendorAdd,
    activeClient, setActiveClient, openLeadForClient,
    openEstimateForClient, openClientInvoice, openInvoiceFromEstimate, openInvoiceDoc,
    askDeleteEstimate, askDeleteInvoice,
  } = useApp();

  // { vendor, from } while the "Move card" picker is open.
  const [moving, setMoving] = useState(null);
  // The card currently being dragged, and the column being hovered over.
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const baseMenu = {
    onOpen: setActiveVendor,
    onEdit: openVendorEdit,
    onWatch: handleToggleWatch,
    onDelete: (v) => setConfirmDelete({ type: 'vendor', id: v._id, label: v.businessName }),
    showToast,
  };
  // Board cards can be moved — they're in a column, so "move" has a meaning.
  const menuItems = vendorMenuItems({
    ...baseMenu,
    onMove: (v, from) => setMoving({ vendor: v, from }),
  });
  // List rows aren't in a column, so Move is left off there (a tech with two
  // trades has no single "from" to move out of).
  const listMenuItems = vendorMenuItems(baseMenu);

  const dropOnColumn = (trade) => {
    setDragOver(null);
    if (!dragging) return;
    const { vendor, from } = dragging;
    setDragging(null);
    if (trade === from || trade === NO_TRADE) return;
    handleMoveVendorTrade(vendor, from === NO_TRADE ? null : from, trade);
  };

  const openAppointment = (req) => {
    setActiveRequest(req);
    setAppointmentInput(toLocalInput(req.appointmentAt));
    setModal('appointment');
  };

  const visibleLedger = ledgerVendorFilter
    ? ledger.filter(e => (e.vendorId?._id || e.vendorId) === ledgerVendorFilter)
    : ledger;
  const totals = ledgerTotals(visibleLedger);
  // Vendors who can't be paid right now — surfaced on the ledger so it's clear
  // before a check gets cut.
  const blockedVendors = vendors.filter(v => v.compliance && !v.compliance.canBePaid);

  return (
    <div className={`screen ${currentScreen === 'screen-admin-dashboard' ? 'active' : ''}`}>
      <nav className="nav">
        <div className="nav-brand"><button type="button" className="brandmark" aria-label="Home" onClick={() => goTo('screen-home')}><Seal size={30} /></button> <span style={{ fontSize: 11, background: '#1e3a5f', color: '#60a5fa', padding: '2px 8px', borderRadius: 50, marginLeft: 6 }}>Admin</span></div>
        {/* Add Lead used to live up here, on every tab, whether or not leads
            were even on screen. It now sits inside the Leads tab with the list
            it adds to — the same place + New Project and + New Invoice sit —
            which also gives the nav back a row on a phone. */}
        <div className="nav-right">
          <button className="btn btn-ghost btn-sm" style={{ color: 'white', borderColor: '#334155' }} onClick={() => setModal('email')}>Email</button>
          <button className="btn btn-ghost btn-sm" style={{ color: 'white', borderColor: '#334155' }} onClick={() => setModal('password')}>Password</button>
          <button className="btn btn-ghost btn-sm" style={{ color: 'white', borderColor: '#334155' }} onClick={logout}>Logout</button>
        </div>
      </nav>

      {/* Big Leads / Clients / Vendors switcher. Seven tabs do not fit across a
          phone, so the strip scrolls sideways rather than clipping Texting off
          the end of the screen. */}
      <div className="admin-segs">
        <button className={`admin-seg ${adminView === 'leads' ? 'active' : ''}`} onClick={() => setAdminView('leads')}>Leads</button>
        <button className={`admin-seg ${adminView === 'clients' ? 'active' : ''}`} onClick={() => { setAdminView('clients'); setActiveClient(null); }}>Clients ({clients.length})</button>
        <button className={`admin-seg ${adminView === 'techs' ? 'active' : ''}`} onClick={() => { setAdminView('techs'); setActiveVendor(null); }}>
          Vendors ({vendors.length}){blockedVendors.length > 0 && <span style={{ color: 'var(--red)' }}> ●{blockedVendors.length}</span>}
        </button>
        <button className={`admin-seg ${adminView === 'ledger' ? 'active' : ''}`} onClick={() => setAdminView('ledger')}>Ledger ({ledger.length})</button>
        <button className={`admin-seg ${adminView === 'projects' ? 'active' : ''}`} onClick={() => { setAdminView('projects'); setActiveProject(null); }}>
          Projects ({projects.length})
        </button>
        <button className={`admin-seg ${adminView === 'accounts' ? 'active' : ''}`} onClick={() => { setAdminView('accounts'); loadAdminAccounts(); }}>
          Accounts ({(adminAccounts || []).length})
        </button>
        <button className={`admin-seg ${adminView === 'texting' ? 'active' : ''}`} onClick={() => { setAdminView('texting'); refreshSmsStatus(); }}>
          Texting
        </button>
        <button className={`admin-seg ${adminView === 'marketing' ? 'active' : ''}`} onClick={() => setAdminView('marketing')}>Marketing</button>
      </div>

      {adminView === 'marketing' && <Marketing />}

      {adminView === 'texting' && (
        <div style={{ padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20 }}>Inbound texts</div>
            <button className="btn btn-outline btn-sm" onClick={refreshSmsStatus} disabled={isLoadingSms}>
              {isLoadingSms ? 'Checking…' : 'Refresh'}
            </button>
          </div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 14, lineHeight: 1.5 }}>
            Every text sent to (385) 338-3625 and what happened to it. If a reply
            is missing from a job thread, it is on this list with the reason.
          </div>

          <div style={{ marginBottom: 14, fontSize: 13 }}>
            Relay is {smsStatus.enabled
              ? <span style={{ color: 'var(--green)' }}>switched on</span>
              : <span style={{ color: 'var(--red)' }}>off — the Twilio keys are missing in Render</span>}
          </div>

          <InstallCard />

          {/* Push notifications: the ones that arrive with the site shut. */}
          <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 14, marginBottom: 18, background: 'var(--white)' }}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, marginBottom: 6 }}>
              Notifications on this device
            </div>
            {pushBlockedReason() ? (
              <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
                {pushBlockedReason()}
              </div>
            ) : (
              <>
                <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 10, lineHeight: 1.5 }}>
                  {pushOn
                    ? 'This device will buzz when a message arrives, even with the site closed.'
                    : 'Get alerted when a customer or technician messages you, even when the site is closed.'}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className={`btn btn-sm ${pushOn ? 'btn-ghost' : 'btn-primary'}`} disabled={pushBusy} onClick={togglePush}>
                    {pushBusy ? 'Working…' : pushOn ? 'Turn off' : 'Turn on notifications'}
                  </button>
                  {pushOn && (
                    <button className="btn btn-outline btn-sm" onClick={sendTestPush}>Send me a test</button>
                  )}
                </div>
              </>
            )}
            {/* Only reachable on Android or a computer — iPhone without the app
                installed is a hard block above, and with it installed there is
                nothing left to suggest. So this is a genuine option, not a
                requirement, and shouldn't be worded like one. */}
            {!isInstalled() && !pushBlockedReason() && (
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 10, lineHeight: 1.5 }}>
                Notifications work right here, no install needed. You can also add
                the site to your home screen for an app icon and a cleaner window.
              </div>
            )}
          </div>

          {smsStatus.loaded && smsStatus.events?.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, border: '1px solid var(--border)', borderRadius: 12, padding: 16, background: 'var(--white)' }}>
              Nothing has arrived yet. If you have texted the number and this stays
              empty, Twilio is not calling the website at all — check the Messaging
              Service in Twilio (Integration → Send a webhook) and make sure it
              posts to <code>https://quickpropertyservices.com/api/messages/sms-inbound</code>.
              Setting it on the phone number instead of the Messaging Service looks
              right in the console but does nothing.
            </div>
          )}

          {smsStatus.events?.map(e => {
            const good = e.outcome === 'filed';
            const label = {
              filed: 'Saved to the thread',
              bad_signature: 'Rejected — webhook URL mismatch',
              not_configured: 'Texting was switched off',
              empty: 'Empty message',
              no_match: 'Number not recognised',
              no_job: 'No job to file it against',
              error: 'Error',
            }[e.outcome] || e.outcome;
            return (
              <div key={e._id} style={{ border: '1px solid var(--border)', borderLeft: `3px solid ${good ? 'var(--green)' : 'var(--red)'}`, borderRadius: 10, padding: '10px 12px', marginBottom: 8, background: 'var(--white)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 14 }}>{e.from || 'unknown number'}</span>
                  <span style={{ fontSize: 12, color: good ? 'var(--green)' : 'var(--red)' }}>{label}</span>
                </div>
                {e.bodyPreview && <div style={{ fontSize: 13, marginTop: 5, whiteSpace: 'pre-wrap' }}>{e.bodyPreview}</div>}
                {e.detail && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 5, lineHeight: 1.5 }}>{e.detail}</div>}
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 5 }}>{fmtDateTime(e.createdAt)}</div>
              </div>
            );
          })}
        </div>
      )}

      {adminView === 'leads' && (
      <>
      <div style={{ background: 'white', borderBottom: '1px solid var(--border)', padding: '0 20px', display: 'flex', gap: 0, overflowX: 'auto' }}>
        {['all', 'new', 'estimate-sent', 'approved', 'assigned', 'completed', 'invoiced', 'paid'].map(t => (
          <button key={t} className={`tab-btn ${activeTab === t ? 'active' : ''}`} onClick={() => setActiveTab(t)} style={{ whiteSpace: 'nowrap' }}>
            {t === 'all' ? 'All' : t === 'estimate-sent' ? 'Est. Sent' : t.charAt(0).toUpperCase() + t.slice(1)}
            {' '}
            <span style={{ fontSize: 11, background: 'var(--border)', borderRadius: 50, padding: '1px 6px' }}>
              {requests.filter(r => t === 'all' ? true : r.status === t).length}
            </span>
          </button>
        ))}
      </div>

      {/* Sits under the filters rather than inside the scrolling list, so it is
          still there after scrolling past a screenful of leads. */}
      <div style={{ background: 'white', borderBottom: '1px solid var(--border)', padding: '10px 20px' }}>
        <button className="btn btn-primary btn-sm" onClick={() => setModal('addLead')}>+ Add Lead</button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
        {requests
          .filter(r => activeTab === 'all' ? true : r.status === activeTab)
          // Emergencies first, then anything blocked waiting on a manager's
          // approval, then newest. The list is otherwise in date order and an
          // emergency filed on Tuesday would sit below Wednesday's dripping tap.
          .slice()
          .sort((a, b) => {
            const rank = (r) => (r.priority === 'emergency' ? 0 : r.nteStatus === 'exceeded' ? 1 : r.priority === 'urgent' ? 2 : 3);
            return rank(a) - rank(b) || new Date(b.createdAt) - new Date(a.createdAt);
          })
          .map(req => (
            <SwipeToDelete key={req._id} onDelete={() => setConfirmDelete({ type: 'lead', id: req._id, label: `${req.homeownerId?.name || req.fullName}${req.serviceType ? ' · ' + req.serviceType : ''}` })}>
            <div className="lead-card" style={{ position: 'relative' }}>
              {/* The job's own menu, the same "⋯" the vendor board uses.
                  Work order first because it is the one Julian reaches for
                  standing in a driveway; the estimate PDF only appears once
                  there is an estimate to print. */}
              <CardMenu
                label={`Actions for ${req.homeownerId?.name || req.fullName}`}
                items={[
                  {
                    key: 'wo',
                    label: req.workOrder?.number ? `Work order ${req.workOrder.number}` : 'Create work order',
                    icon: MenuIcons.doc,
                    onSelect: () => openWorkOrder(req),
                  },
                  {
                    key: 'wo-del',
                    label: 'Delete work order',
                    icon: MenuIcons.trash,
                    danger: true,
                    // Only when there is one to take back. The number it used
                    // is never handed out again — see the route.
                    hidden: !req.workOrder?.number,
                    onSelect: () => setConfirmDelete({ type: 'work order', id: req._id, label: req.workOrder?.number }),
                  },
                  {
                    key: 'est-edit',
                    label: 'View / edit estimate',
                    icon: MenuIcons.pencil,
                    hidden: !req.estimate?.amount,
                    onSelect: () => openEstimate(req),
                  },
                  {
                    key: 'est',
                    label: 'View & print estimate',
                    icon: MenuIcons.printer,
                    hidden: !req.estimate?.amount,
                    onSelect: () => setPrintDoc({ request: req, kind: 'estimate' }),
                  },
                  {
                    key: 'del',
                    label: 'Delete lead',
                    icon: MenuIcons.trash,
                    danger: true,
                    onSelect: () => setConfirmDelete({ type: 'lead', id: req._id, label: `${req.homeownerId?.name || req.fullName}${req.serviceType ? ' · ' + req.serviceType : ''}` }),
                  },
                ]}
              />
              <div className="lead-name" style={{ paddingRight: 30 }}>
                {req.homeownerId?.name || req.fullName}
                <span style={{ marginLeft: 8 }}>{statusTag(req.status)}</span>
                <span className={`urgency-dot ${urgencyDot(req.urgency)}`} style={{ marginLeft: 8 }}></span>
              </div>
              {/* Where this came from, when it came from a management company.
                  An emergency is red and first, because the whole point of the
                  priority is that it changes what Julian does next. A limit
                  that has been blown is amber and says DON'T DISPATCH — the
                  work is agreed but the money is not. */}
              {req.accountId && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                  {req.priority === 'emergency' && <span className="tag tag-red">Emergency</span>}
                  {req.priority === 'urgent' && <span className="tag tag-amber">Urgent</span>}
                  <span className="tag tag-purple">
                    {req.accountId?.name || 'Commercial account'}
                  </span>
                  {req.unitLabel && <span className="tag tag-slate">{req.unitLabel}</span>}
                  {req.nteStatus === 'exceeded' && (
                    <span className="tag tag-amber">Over limit — awaiting their approval</span>
                  )}
                  {req.nte?.amount != null && req.nteStatus !== 'exceeded' && (
                    <span className="tag tag-slate">Limit {fmtMoney(req.nte.amount)}</span>
                  )}
                  {/* Who they asked for. Only until somebody is actually on it
                      — once assigned, the assignment is the fact and the
                      request is history. */}
                  {req.requestedVendorName && !req.assignedVendorId && (
                    <span className="tag tag-blue">Asked for {req.requestedVendorName}</span>
                  )}
                </div>
              )}
              <div className="lead-job">{req.serviceType} · {req.serviceAddress}{req.zip ? `, ${req.zip}` : ''}</div>
              {/* Paperwork on the job — the move-out inspection a turn is
                  quoted against, a permit, a signed change. */}
              {(req.documents || []).length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '6px 0' }}>
                  {req.documents.map((d, i) => (
                    <a key={d._id || i} className="btn btn-outline btn-sm" href={d.url} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                      {d.name || 'Document'}
                    </a>
                  ))}
                </div>
              )}
              <div style={{ fontSize: 12, color: 'var(--slate)', margin: '4px 0 6px' }}>
                {/* Older leads carry the literal string "undefined" from a
                    signed-in customer with no phone on their account; new ones
                    say "No phone given". Neither is something to ring. */}
                {req.phone && !['undefined', 'null', 'No phone given'].includes(req.phone)
                  ? <a href={`tel:${req.phone}`} style={{ color: 'var(--blue)', textDecoration: 'none' }}>{req.phone}</a>
                  : <span style={{ color: 'var(--amber)' }}>No phone given</span>}
                {req.email && <> · {req.email}</>}
              </div>
              {/* What they actually asked for, a line each with its own
                  photos. A lead with three jobs on it is three jobs to price
                  and three to check off, and the strip under each line is the
                  only way to tell which photo shows which. Leads taken before
                  lines existed have none and fall back to the description. */}
              {req.items?.length > 0 ? (
                <div style={{ margin: '0 0 8px' }}>
                  {req.items.map((it, i) => (
                    <div key={i} style={{ padding: '7px 0', borderTop: i ? '1px solid var(--border)' : 'none' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)' }}>
                        {req.items.length > 1 && <span style={{ color: 'var(--muted)' }}>{i + 1}. </span>}
                        {it.title || it.serviceType}
                        {it.serviceType && it.serviceType !== it.title && (
                          <span className="tag tag-slate" style={{ marginLeft: 6 }}>{it.serviceType}</span>
                        )}
                      </div>
                      {it.description && it.description !== it.title && (
                        <div style={{ fontSize: 13, color: 'var(--slate)', lineHeight: 1.4, marginTop: 2 }}>{it.description}</div>
                      )}
                      <MediaStrip urls={it.photos} label="Photos" />
                    </div>
                  ))}
                </div>
              ) : req.description && (
                <div style={{ fontSize: 13, color: 'var(--slate)', margin: '0 0 8px', lineHeight: 1.4 }}>{req.description}</div>
              )}
              <div className="lead-meta">
                <span className={`tag ${req.areaTag === 'Outside Area' ? 'tag-amber' : 'tag-green'}`}>{req.areaTag || 'Primary Area'}{req.city ? ` · ${req.city}` : ''}</span>
                <span className="tag tag-slate">via {req.leadSource || 'Website'}</span>
                <span className="tag tag-slate">{req.urgency}</span>
                {req.assignedVendorId && <span className="tag tag-teal">Tech: {req.assignedVendorId.businessName}</span>}
              </div>
              <div style={{ fontSize: 12, margin: '8px 0 2px', fontWeight: 600, color: req.appointmentAt ? 'var(--green)' : 'var(--amber)' }}>
                {req.appointmentAt ? `Appt: ${fmtDateTime(req.appointmentAt)}` : 'No appointment set'}
              </div>
              <div className="lead-footer">
                <span className="lead-amount">{req.estimate ? fmtMoney(req.estimate.amount) : '—'}</span>
                <span className="lead-date">{fmtDate(req.createdAt)}</span>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                <MessageButton request={req} />
                <button className="btn btn-outline btn-sm" onClick={() => openAppointment(req)}>{req.appointmentAt ? 'Reschedule' : 'Set Appointment'}</button>
                {/* The estimate. On a new lead it opens empty but for a
                    priced row per job the customer listed; once one exists the
                    same sheet opens with it loaded, to read or to correct. */}
                <button className={`btn btn-sm ${req.estimate?.amount ? 'btn-outline' : 'btn-primary'}`} onClick={() => openEstimate(req)}>
                  {req.estimate?.amount ? 'View / edit estimate' : 'Submit Estimate'}
                </button>

                {/* Most jobs are agreed in a driveway or down the phone. Making
                    Julian wait for the customer to log in and tap a button —
                    when they have already said yes to his face — is the app
                    arguing with what actually happened. Their portal is told
                    who approved it. */}
                {req.estimateStatus === 'pending' && (
                  <>
                    <button className="btn btn-green btn-sm" onClick={() => handleAdminApproveEstimate(req, true)}>Approve for customer</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => handleAdminApproveEstimate(req, false)}>Mark declined</button>
                  </>
                )}

                {/* Assigning is no longer gated on the customer having
                    approved: Julian books the crew when he books the crew. */}
                {/* Falls back to whoever the company asked for, so the common
                    case is one tap and no scrolling. */}
                {!['completed', 'invoiced', 'paid'].includes(req.status) && (
                  <button className="btn btn-green btn-sm" onClick={() => { setActiveRequest(req); setAssignVendorId(String(req.assignedVendorId?._id || req.assignedVendorId || req.requestedVendorId || '')); setModal('assign'); }}>
                    {req.assignedVendorId ? 'Reassign tech' : 'Assign tech'}
                  </button>
                )}
                {req.status === 'invoiced' && (
                  <button className="btn btn-green btn-sm" onClick={() => handleApproveInvoice(req._id)}>Approve Invoice ({fmtMoney(req.invoice?.amount)})</button>
                )}
                {req.estimateStatus === 'pending' && (
                  <span style={{ fontSize: 12, color: 'var(--muted)', padding: '5px 0' }}>Awaiting homeowner approval</span>
                )}
              </div>
              {/* Only when the lines didn't already show them — req.photos is
                  the flattened roll-up of exactly the same files. */}
              {!req.items?.some(it => it.photos?.length) && <MediaStrip urls={req.photos} label="Photos" />}
              <MediaStrip urls={req.completionPhotos} label="Completion photos" />
            </div>
            </SwipeToDelete>
          ))}
        {requests.filter(r => activeTab === 'all' ? true : r.status === activeTab).length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--muted)' }}>No requests in this category</div>
        )}
      </div>
      </>
      )}

      {/* ── Projects ─────────────────────────────────────────── */}
      {adminView === 'projects' && activeProject && <AdminProjectDetail project={activeProject} />}

      {adminView === 'projects' && !activeProject && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          <button className="btn btn-primary btn-sm" style={{ marginBottom: 14 }} onClick={() => openProjectForm(null)}>+ New Project</button>

          {/* Estimates a tech has sent in and is waiting on */}
          {estimates.filter(e => e.status === 'submitted').length > 0 && (
            <>
              <div className="sec-label">Estimates waiting on you</div>
              {estimates.filter(e => e.status === 'submitted').map(e => (
                <div key={e._id} className="lead-card" style={{ cursor: 'default', borderLeft: '3px solid var(--amber)' }}>
                  <div className="lead-name">{e.trade || 'Work'} · {e.vendorId?.businessName}</div>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '8px 0' }}>
                    {[['Their price', e.techTotal, 'var(--navy)'], [`+${e.markupPercent}% markup`, e.markupAmount, 'var(--amber)'], ['Client price', e.clientTotal, 'var(--green)']].map(([label, value, color]) => (
                      <div key={label} style={{ flex: 1, minWidth: 90 }}>
                        <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)' }}>{label}</div>
                        <div style={{ fontSize: 15, color }}>{fmtMoney(value)}</div>
                      </div>
                    ))}
                  </div>
                  {e.items?.map((i, idx) => (
                    <div key={idx} style={{ fontSize: 12, color: 'var(--slate)', display: 'flex', justifyContent: 'space-between' }}>
                      <span>{i.description} <span className="tag tag-slate">{i.kind}</span></span>
                      <span>{fmtMoney(i.total)}</span>
                    </div>
                  ))}
                  <div style={{ marginTop: 10 }}>
                    <select
                      defaultValue=""
                      onChange={ev => { if (ev.target.value) handleApproveEstimateToProject(e, ev.target.value); }}
                      style={{ padding: '6px 10px', border: '1.5px solid var(--border)', borderRadius: 'var(--radius-sm)', background: 'var(--white)', color: 'var(--navy)', fontFamily: 'inherit', maxWidth: 260 }}
                    >
                      <option value="">Approve onto a project…</option>
                      {projects.map(pr => <option key={pr._id} value={pr._id}>{pr.title}</option>)}
                    </select>
                  </div>
                </div>
              ))}
            </>
          )}

          {projects.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px 20px', color: 'var(--muted)', lineHeight: 1.5 }}>
              No projects yet. A project is one property job made of many trades — create one, then add an invoice
              line per trade.
            </div>
          ) : projects.map(pr => (
            <div key={pr._id} className="lead-card" onClick={(ev) => { if (!ev.target.closest('button')) setActiveProject(pr); }}>
              <div className="lead-name">
                {pr.title}
                <span style={{ float: 'right', color: 'var(--muted)', fontSize: 12 }}>→</span>
              </div>
              <div className="lead-job">{pr.homeownerId?.name || pr.clientName || 'No client linked'}{pr.address ? ` · ${pr.address}` : ''}</div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', padding: '10px 0', borderTop: '1px solid var(--border)', margin: '8px 0 0' }}>
                {[['Invoiced', pr.invoicedTotal, 'var(--navy)'], ['Received', pr.receivedTotal, 'var(--green)'], ['Owed', pr.owedByClient, 'var(--red)'], ['Profit', pr.profit, pr.profit >= 0 ? 'var(--green)' : 'var(--red)']].map(([label, value, color]) => (
                  <div key={label} style={{ flex: 1, minWidth: 80 }}>
                    <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)' }}>{label}</div>
                    <div style={{ fontSize: 14, color }}>{fmtMoney(value)}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button className="btn btn-outline btn-sm" onClick={() => openProjectForm(pr)}>Edit</button>
                <button className="btn btn-ghost btn-sm" onClick={() => handleDeleteProject(pr)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Vendor Ledger ────────────────────────────────────── */}
      {adminView === 'ledger' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {/* The spreadsheet's "Total Amount Paid" / "Total Amount Owed" cells */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
            {[
              ['Total Invoiced', totals.invoiced, '#f1f5f9', 'var(--slate)'],
              ['Total Amount Paid', totals.paid, 'var(--green-light)', 'var(--green)'],
              ['Total Amount Owed', totals.owed, 'var(--red-light)', 'var(--red)'],
            ].map(([label, value, bg, color]) => (
              <div key={label} style={{ flex: 1, minWidth: 110, background: bg, borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color, opacity: .85 }}>{label}</div>
                <div style={{ fontSize: 19, color }}>{fmtMoney(value)}</div>
              </div>
            ))}
          </div>

          {blockedVendors.length > 0 && (
            <div className="alert alert-red">
              <div>
                <strong>{blockedVendors.length} vendor{blockedVendors.length === 1 ? '' : 's'} can't be paid — paperwork not current.</strong>
                <div style={{ marginTop: 4 }}>
                  {blockedVendors.map(v => `${v.businessName} (${v.compliance.blockers.join(', ')})`).join(' · ')}
                </div>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
            <button className="btn btn-primary btn-sm" onClick={() => openLedgerForm(null)}>+ New Invoice</button>
            <button className="btn btn-outline btn-sm" onClick={handleExportLedger}>Export CSV</button>
            <select value={ledgerVendorFilter} onChange={e => setLedgerVendorFilter(e.target.value)} style={{ maxWidth: 220, padding: '6px 10px', border: '1.5px solid var(--border)', borderRadius: 'var(--radius-sm)', background: 'var(--white)', color: 'var(--navy)', fontFamily: 'inherit' }}>
              <option value="">All vendors</option>
              {vendors.map(v => <option key={v._id} value={v._id}>{v.businessName}</option>)}
            </select>
          </div>

          {visibleLedger.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px 20px', color: 'var(--muted)', lineHeight: 1.5 }}>
              No ledger entries yet. Vendors create these when they submit an invoice, or add one yourself with + New Invoice.
            </div>
          ) : (
            visibleLedger.map(entry => (
              <LedgerCard
                key={entry._id}
                entry={entry}
                role="admin"
                onRecordPayment={openRecordPayment}
                onDeletePayment={handleDeletePayment}
                onAddProgress={openProgress}
                onEdit={openLedgerForm}
                onDelete={handleDeleteLedgerEntry}
              />
            ))
          )}
        </div>
      )}

      {/* A vendor's own page: their assigned jobs and everything they've filed */}
      {adminView === 'techs' && activeVendor && (() => {
        // Re-read from `vendors` so the profile reflects the latest load.
        const v = vendors.find(x => x._id === activeVendor._id) || activeVendor;
        const p = v.profile || {};
        const phone = v.phone || v.userId?.phone;
        const email = v.email || v.userId?.email;
        const jobs = requests.filter(r => (r.assignedVendorId?._id || r.assignedVendorId) === v._id);
        const entries = ledger.filter(e => (e.vendorId?._id || e.vendorId) === v._id);
        const eTotals = ledgerTotals(entries);
        // Every progress report this vendor has filed, newest first, with the
        // invoice it belongs to.
        const updates = entries
          .flatMap(e => (e.progress || []).map(u => ({ ...u, entry: e })))
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        return (
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
            <button className="btn btn-ghost btn-sm" style={{ marginBottom: 12 }} onClick={() => setActiveVendor(null)}>← All vendors</button>

            {/* The tech's details, read top to bottom with the label in front
                of every line — the same order Julian reads them off a phone
                call. Blank lines still show their label, so it's obvious at a
                glance what's missing rather than looking like it isn't asked. */}
            <div className="lead-card vsheet" style={{ cursor: 'default' }}>
              <div className="vsheet-head">
                <button className="btn btn-outline btn-sm" onClick={() => openVendorEdit(v)}>Edit</button>
              </div>

              {[
                ['Name', v.userId?.name],
                ['Business Name', v.businessName],
                ['Occupation', p.occupation || v.specialty || tradesOf(v).join(', ')],
                ['Phone Number', phone && <a href={`tel:${phone}`}>{phone}</a>],
                ['Email Address', email && <a href={`mailto:${email}`}>{email}</a>],
                ['Business Address', p.address],
                ['Home Address if any', p.homeAddress],
              ].map(([label, value]) => (
                <div className="vsheet-row" key={label}>
                  <span className="vsheet-label">{label}:</span>
                  <span className={`vsheet-value${value ? '' : ' empty'}`}>{value || '—'}</span>
                </div>
              ))}

              {p.completedAt && (
                <div className="lead-meta" style={{ marginTop: 12 }}>
                  {p.yearsExperience && <span className="tag tag-slate">{p.yearsExperience} yrs experience</span>}
                  <span className={`tag ${p.hasTools === 'Yes' ? 'tag-green' : 'tag-amber'}`}>Tools: {p.hasTools || '—'}</span>
                  <span className={`tag ${p.licensed === 'Yes' ? 'tag-green' : 'tag-amber'}`}>Licensed: {p.licensed || '—'}</span>
                  <span className={`tag ${p.insured === 'Yes' ? 'tag-green' : 'tag-amber'}`}>Insured: {p.insured || '—'}</span>
                </div>
              )}
              {(p.hourlyRate || p.sqftRate) && (
                <div style={{ fontSize: 13, marginTop: 8 }}>Rates: {[p.hourlyRate, p.sqftRate].filter(Boolean).join(' · ')}</div>
              )}
            </div>

            {/* Paperwork */}
            {v.compliance && (
              <div style={{ margin: '10px 0', padding: '10px 12px', borderRadius: 8, background: v.compliance.canBePaid ? 'var(--green-light)' : 'var(--red-light)' }}>
                <div style={{ fontSize: 12, marginBottom: 6, color: v.compliance.canBePaid ? 'var(--green)' : 'var(--red)' }}>
                  {v.compliance.canBePaid ? 'Clear to be paid' : 'Payments blocked — paperwork not current'}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {v.compliance.items.map(item => <span key={item.type}>{credentialTag(item)}</span>)}
                  {v.compliance.extras?.map(item => <span key={item._id}>{credentialTag(item)}</span>)}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                  <button className="btn btn-outline btn-sm" onClick={() => openCredential({ type: 'insurance' }, v._id)}>Add / Renew a Document</button>
                  {p.documents?.map((d, i) => (
                    <button key={i} className="btn btn-outline btn-sm" onClick={() => window.open(d, '_blank')}>Application Doc {i + 1}</button>
                  ))}
                </div>
              </div>
            )}

            {/* Money with this vendor */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '14px 0' }}>
              {[
                ['Invoiced', eTotals.invoiced, '#f1f5f9', 'var(--slate)'],
                ['Paid', eTotals.paid, 'var(--green-light)', 'var(--green)'],
                ['Owed', eTotals.owed, 'var(--red-light)', 'var(--red)'],
              ].map(([label, value, bg, color]) => (
                <div key={label} style={{ flex: 1, minWidth: 100, background: bg, borderRadius: 8, padding: '10px 12px' }}>
                  <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color, opacity: .85 }}>{label}</div>
                  <div style={{ fontSize: 18, color }}>{fmtMoney(value)}</div>
                </div>
              ))}
            </div>

            {/* Assigned jobs */}
            <div className="sec-label" style={{ marginTop: 16 }}>Assigned Jobs ({jobs.length})</div>
            {jobs.length === 0 ? (
              <div style={{ color: 'var(--muted)', fontSize: 13, padding: '8px 0 12px' }}>Nothing assigned to this vendor yet. Assign them from a lead in the Leads tab.</div>
            ) : jobs.map(r => (
              <div key={r._id} className="lead-card" style={{ cursor: 'default' }}>
                <div className="lead-name">
                  {r.serviceType}
                  <span style={{ marginLeft: 8 }}>{statusTag(r.status)}</span>
                </div>
                <div className="lead-job">{r.homeownerId?.name || r.fullName} · {r.serviceAddress}</div>
                {r.description && <div style={{ fontSize: 13, color: 'var(--slate)', margin: '6px 0' }}>{r.description}</div>}
                <div className="lead-footer">
                  <span className="lead-amount">{r.estimate ? fmtMoney(r.estimate.amount) : '—'}</span>
                  <span className="lead-date">{r.appointmentAt ? fmtDateTime(r.appointmentAt) : fmtDate(r.createdAt)}</span>
                </div>
                <MediaStrip urls={r.completionPhotos} label="Completion photos" />
              </div>
            ))}

            {/* Progress reports across every invoice */}
            <div className="sec-label" style={{ marginTop: 16 }}>Progress Reports ({updates.length})</div>
            {updates.length === 0 ? (
              <div style={{ color: 'var(--muted)', fontSize: 13, padding: '8px 0 12px' }}>No progress reports filed yet. Vendors post these against an invoice from their Ledger tab.</div>
            ) : (
              <div className="lead-card" style={{ cursor: 'default' }}>
                {updates.map((u, i) => (
                  <div key={u._id || i} style={{ paddingBottom: 12, marginBottom: 12, borderBottom: i < updates.length - 1 ? '1px dashed var(--border)' : 'none' }}>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                      {fmtDate(u.createdAt)} · {u.entry.invoiceNumber}
                      {u.percentComplete != null && <> · <span style={{ color: 'var(--green)' }}>{u.percentComplete}%</span></>}
                      {u.authorRole === 'admin' && <> · posted by QPS</>}
                    </div>
                    {u.note && <div style={{ fontSize: 13, margin: '3px 0 6px', lineHeight: 1.4 }}>{u.note}</div>}
                    <MediaStrip urls={u.photos} label="Photos" />
                  </div>
                ))}
              </div>
            )}

            {/* Their invoices */}
            <div className="sec-label" style={{ marginTop: 16 }}>Invoices ({entries.length})</div>
            {entries.length === 0 ? (
              <div style={{ color: 'var(--muted)', fontSize: 13, padding: '8px 0 40px' }}>No invoices from this vendor yet.</div>
            ) : entries.map(entry => (
              <LedgerCard
                key={entry._id}
                entry={entry}
                role="admin"
                onRecordPayment={openRecordPayment}
                onDeletePayment={handleDeletePayment}
                onAddProgress={openProgress}
                onEdit={openLedgerForm}
                onDelete={handleDeleteLedgerEntry}
              />
            ))}
          </div>
        );
      })()}

      {adminView === 'techs' && !activeVendor && (
      <>
      {/* Same row, same reasoning, as + Add Lead and + Add Client: with the
          list it adds to, and outside the scroll so a long board can't hide it. */}
      <div style={{ background: 'white', borderBottom: '1px solid var(--border)', padding: '10px 20px' }}>
        <button className="btn btn-primary btn-sm" onClick={openVendorAdd}>+ Add Technician</button>
      </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {vendors.length > 0 && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              <button className={`btn btn-sm ${vendorLayout === 'board' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setVendorLayout('board')}>Board</button>
              <button className={`btn btn-sm ${vendorLayout === 'list' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setVendorLayout('list')}>List</button>
              <span style={{ fontSize: 12, color: 'var(--muted)', alignSelf: 'center' }}>
                {vendorLayout === 'board' ? 'Tap a card to open that vendor' : 'Swipe a row left to delete'}
              </span>
            </div>
          )}

          {/* Move card — the picker. Works on a phone, where dragging inside a
              side-scrolling board fights the scroll gesture. */}
          {moving && (
            <>
              <div className="modal active" style={{ alignItems: 'center' }}>
                <div className="modal-box" style={{ maxWidth: 420, borderRadius: 18, margin: '0 16px', padding: '22px 18px 26px' }}>
                  <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 21, marginBottom: 4 }}>
                    Move {moving.vendor.businessName}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16, lineHeight: 1.5 }}>
                    {moving.from && moving.from !== NO_TRADE
                      ? <>Currently under <strong style={{ color: 'var(--slate)' }}>{moving.from}</strong>. Picking a trade moves this card there.</>
                      : <>No trade on file. Picking a trade files them under it.</>}
                  </div>
                  <div style={{ maxHeight: '46vh', overflowY: 'auto', margin: '0 -4px' }}>
                    {TAXONOMY.map(x => x.cat)
                      .filter(cat => cat !== moving.from)
                      .map(cat => (
                        <button
                          key={cat}
                          className="cardmenu-item"
                          style={{ width: '100%', padding: '12px 12px', fontSize: 15 }}
                          onClick={() => {
                            handleMoveVendorTrade(moving.vendor, moving.from === NO_TRADE ? null : moving.from, cat);
                            setMoving(null);
                          }}
                        >
                          <span className="cardmenu-ico">{MenuIcons.move}</span>
                          <span>{cat}</span>
                        </button>
                      ))}
                  </div>
                  <button className="btn btn-ghost btn-lg" style={{ marginTop: 14 }} onClick={() => setMoving(null)}>Cancel</button>
                </div>
              </div>
              <div className="overlay active" onClick={() => setMoving(null)}></div>
            </>
          )}

          {vendorLayout === 'board' && vendors.length > 0 && (
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 12, fontSize: 12, color: 'var(--muted)' }}>
              {[
                ['var(--green)', 'Good to use'],
                ['var(--amber)', 'Renewal coming up'],
                ['var(--red)', "Can't use — paperwork not current"],
              ].map(([color, text]) => (
                <span key={text} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 9, height: 9, borderRadius: 50, background: color, display: 'inline-block' }} />
                  {text}
                </span>
              ))}
            </div>
          )}

          {/* Trello-style board: a column per trade, a card per vendor */}
          {vendorLayout === 'board' && vendors.length > 0 && (
            <div className="vboard">
              {vendorColumns(vendors).map(([trade, list]) => (
                <div
                  key={trade}
                  className={`vboard-col${dragOver === trade && dragging?.from !== trade ? ' drop-target' : ''}`}
                  onDragOver={(e) => { if (dragging && trade !== NO_TRADE) { e.preventDefault(); setDragOver(trade); } }}
                  onDragLeave={() => setDragOver(o => (o === trade ? null : o))}
                  onDrop={(e) => { e.preventDefault(); dropOnColumn(trade); }}
                >
                  <div className="vboard-head">
                    <span>{trade}</span>
                    <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      {['valid', 'expiring', 'blocked'].map(st => {
                        const n = list.filter(v => (v.compliance?.status || 'blocked') === st).length;
                        if (!n) return null;
                        const color = { valid: 'var(--green)', expiring: 'var(--amber)', blocked: 'var(--red)' }[st];
                        return <span key={st} className="vboard-count" style={{ color }}>{n}</span>;
                      })}
                    </span>
                  </div>
                  <div className="vboard-list">
                    {list.map(v => (
                      <VendorCard
                        key={v._id} v={v} column={trade}
                        onOpen={setActiveVendor} menuItems={menuItems}
                        dragging={dragging?.vendor?._id === v._id && dragging?.from === trade}
                        onDragStart={() => setDragging({ vendor: v, from: trade })}
                        onDragEnd={() => { setDragging(null); setDragOver(null); }}
                      />
                    ))}
                    {list.length === 0 && <div className="vboard-empty">Nobody here yet</div>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {vendorLayout === 'list' && (vendors.length === 0 ? null : (
            vendors.map(v => {
              const p = v.profile || {};
              const done = !!p.completedAt;
              return (
                <SwipeToDelete key={v._id} onDelete={() => setConfirmDelete({ type: 'vendor', id: v._id, label: v.businessName })}>
                <div
                  className="lead-card"
                  style={{ position: 'relative' }}
                  onClick={(e) => { if (!e.target.closest('button, a')) setActiveVendor(v); }}
                  title="Open this vendor's profile"
                >
                  <CardMenu items={listMenuItems(v)} label={`Actions for ${v.businessName}`} />
                  <div className="lead-name" style={{ paddingRight: 30 }}>
                    {v.watched && <span className="vcard-watch" title="Watching">{MenuIcons.eye}</span>}
                    <span style={{ color: 'var(--blue)', textDecoration: 'underline' }}>{v.businessName}</span>
                    <span className={`tag ${done ? 'tag-green' : 'tag-amber'}`} style={{ marginLeft: 8 }}>{done ? 'Application complete' : 'Application pending'}</span>
                    <span style={{ float: 'right', color: 'var(--muted)', fontSize: 12 }}>→</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--slate)', margin: '4px 0' }}>
                    <a href={`tel:${v.phone || v.userId?.phone}`} style={{ color: 'var(--blue)', textDecoration: 'none' }}>{v.phone || v.userId?.phone || '—'}</a>
                    {' '}· <a href={`mailto:${v.email || v.userId?.email}`} style={{ color: 'var(--blue)', textDecoration: 'none' }}>{v.email || v.userId?.email}</a>
                    {p.address && <> · {p.address}</>}
                  </div>

                  {/* Insurance / license expiry — drives whether this vendor can be paid */}
                  {v.compliance && (
                    <div style={{ margin: '8px 0', padding: '8px 10px', borderRadius: 8, background: v.compliance.canBePaid ? 'var(--green-light)' : 'var(--red-light)' }}>
                      <div style={{ fontSize: 12, marginBottom: 6, color: v.compliance.canBePaid ? 'var(--green)' : 'var(--red)' }}>
                        {v.compliance.canBePaid ? 'Clear to be paid' : 'Payments blocked — paperwork not current'}
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {v.compliance.items.map(item => <span key={item.type}>{credentialTag(item)}</span>)}
                        {v.compliance.extras?.map(item => <span key={item._id}>{credentialTag(item)}</span>)}
                      </div>
                      <button className="btn btn-outline btn-sm" style={{ marginTop: 8 }} onClick={() => openCredential({ type: 'insurance' }, v._id)}>
                        Add / Renew a Document
                      </button>
                    </div>
                  )}

                  {done && (
                    <>
                      <div className="lead-meta" style={{ marginTop: 6 }}>
                        {p.yearsExperience && <span className="tag tag-slate">{p.yearsExperience} yrs experience</span>}
                        <span className={`tag ${p.hasTools === 'Yes' ? 'tag-green' : 'tag-amber'}`}>Tools: {p.hasTools || '—'}</span>
                        <span className={`tag ${p.licensed === 'Yes' ? 'tag-green' : 'tag-amber'}`}>Licensed: {p.licensed || '—'}</span>
                        <span className={`tag ${p.insured === 'Yes' ? 'tag-green' : 'tag-amber'}`}>Insured: {p.insured || '—'}</span>
                        {p.hasCompany === 'Yes' && <span className="tag tag-teal">Company{p.hasCrew === 'Yes' ? ' + crew' : ''}</span>}
                        {p.hasCompany !== 'Yes' && p.hasCrew === 'Yes' && <span className="tag tag-teal">Has crew</span>}
                      </div>
                      {p.expertise?.length > 0 && (
                        <div style={{ fontSize: 13, margin: '6px 0 2px' }}><strong>Expertise:</strong> {[...p.expertise, p.expertiseOther].filter(Boolean).join(', ')}</div>
                      )}
                      {p.skills?.length > 0 && (
                        <div style={{ fontSize: 13, margin: '2px 0' }}><strong>Skills:</strong> {[...p.skills, p.skillsOther].filter(Boolean).join(', ')}</div>
                      )}
                      {(p.hourlyRate || p.sqftRate) && (
                        <div style={{ fontSize: 13, margin: '2px 0' }}><strong>Rates:</strong> {[p.hourlyRate, p.sqftRate].filter(Boolean).join(' · ')}</div>
                      )}
                      {p.companyLink && (
                        <div style={{ fontSize: 13, margin: '2px 0' }}><strong>Link:</strong> <a href={p.companyLink} target="_blank" rel="noreferrer" style={{ color: 'var(--blue)' }}>{p.companyLink}</a></div>
                      )}
                      {p.documents?.length > 0 && (
                        <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                          {p.documents.map((d, i) => (
                            <button key={i} className="btn btn-outline btn-sm" onClick={() => window.open(d, '_blank')}>Document {i + 1}</button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  <div className="lead-footer">
                    <span className="tag tag-slate">Technician</span>
                    <span className="lead-date">Joined {fmtDate(v.createdAt)}</span>
                  </div>
                </div>
                </SwipeToDelete>
              );
            })
          ))}

          {vendors.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--muted)' }}>No technicians yet. They show up here when they sign up in the Technician Portal — or add one yourself with + Add Technician.</div>
          )}
        </div>
      </>
      )}

      {/* ── Commercial accounts ─────────────────────────────────────────
          The companies, their portfolios, and what is open on each. Their
          work orders also appear on the Leads board badged with the company
          name — this tab is for the relationship, that board is for the day. */}
      {adminView === 'accounts' && !adminAccountDetail && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          <button className="btn btn-primary" style={{ marginBottom: 16 }} onClick={openAccountForm}>
            + Add Commercial Account
          </button>
          {(adminAccounts || []).length === 0 ? (
            <div className="lead-card" style={{ cursor: 'default' }}>
              <div className="lead-job">No commercial accounts yet</div>
              <div style={{ fontSize: 13, color: 'var(--slate)', marginTop: 6, lineHeight: 1.5 }}>
                Property managers, HOAs and realtors can sign themselves up at
                quickpropertyservices.com/commercial — or add one yourself with the
                button above and send them a link to set their password.
              </div>
            </div>
          ) : (adminAccounts || []).map(a => (
            <div key={a.id} className="lead-card" onClick={() => openAdminAccount(a.id)}>
              <div className="lead-job">{a.name}</div>
              <div style={{ fontSize: 13, color: 'var(--slate)', margin: '4px 0 6px' }}>
                {a.phone && <a href={`tel:${a.phone}`} style={{ color: 'var(--blue)', textDecoration: 'none' }}>{a.phone}</a>}
                {a.email && <> · {a.email}</>}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <span className="tag tag-purple">{a.propertyCount} properties</span>
                {a.openWorkOrders > 0 && <span className="tag tag-amber">{a.openWorkOrders} open</span>}
                {a.defaultNteAmount != null && <span className="tag tag-slate">Limit {fmtMoney(a.defaultNteAmount)}</span>}
              </div>
              <div className="lead-footer">
                <span className="tag tag-slate">Commercial</span>
                <span className="lead-date">Joined {fmtDate(a.createdAt)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {adminView === 'accounts' && adminAccountDetail && !adminPropertyId && (() => {
        const d = adminAccountDetail;
        return (
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
            <button className="btn btn-ghost btn-sm" style={{ marginBottom: 12 }} onClick={() => openAdminAccount(null)}>← All accounts</button>
            <div className="section-title">{d.account.name}</div>
            <div style={{ fontSize: 13, color: 'var(--slate)', marginBottom: 16 }}>
              {[d.account.phone, d.account.billingEmail || d.account.email].filter(Boolean).join(' · ')}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
              <div className="sec-label" style={{ marginBottom: 0 }}>Who can sign in</div>
              <button className="btn btn-outline btn-sm" onClick={() => openAccountMemberForm(d.account, null)}>+ Add someone</button>
            </div>
            {(d.members || []).length === 0 && (
              <div className="lead-card" style={{ cursor: 'default' }}>
                <div style={{ fontSize: 13, color: 'var(--slate)', lineHeight: 1.5 }}>
                  Nobody can sign in to this account yet. Add someone and they get a link to set their own password.
                </div>
              </div>
            )}
            {(d.members || []).map(m => (
              <div key={m.userId} className="lead-card" style={{ cursor: 'default' }}>
                <div className="lead-job">{m.name}</div>
                <div style={{ fontSize: 13, color: 'var(--slate)', marginTop: 4 }}>
                  {m.phone ? <a href={`tel:${m.phone}`} style={{ color: 'var(--blue)', textDecoration: 'none' }}>{m.phone}</a> : null}
                  {m.phone && m.email ? ' · ' : ''}{m.email}
                </div>
                <div className="lead-footer">
                  <span className={`tag tag-${m.level === 'owner' ? 'navy' : m.level === 'manager' ? 'blue' : 'slate'}`}>{m.level}</span>
                  <span style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-outline btn-sm" onClick={() => openAccountMemberForm(d.account, m)}>Access level</button>
                    <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }}
                      onClick={() => handleRemoveAccountMember(d.account._id || d.account.id, m)}>Remove</button>
                  </span>
                </div>
              </div>
            ))}

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, margin: '18px 0 10px' }}>
              <div className="sec-label" style={{ marginBottom: 0 }}>Properties ({(d.properties || []).length})</div>
              <button className="btn btn-outline btn-sm" onClick={() => openAdminPropertyForm(d.account)}>+ Add a property</button>
            </div>
            {/* Their portfolio is usually taken down the phone on the day they
                come on board, long before anyone at the company has signed in
                to add it themselves. */}
            {(d.properties || []).length === 0 && (
              <div className="lead-card" style={{ cursor: 'default' }}>
                <div style={{ fontSize: 13, color: 'var(--slate)', lineHeight: 1.5 }}>
                  No properties on this account yet. Add one here, or they can add their own — and import a
                  whole spreadsheet of them — once they sign in.
                </div>
              </div>
            )}
            {(d.properties || []).slice(0, 200).map(p => (
              <div key={p._id} className="lead-card" onClick={() => setAdminPropertyId(p._id)}>
                <div className="lead-job">{p.addressLine1}{p.city ? `, ${p.city}` : ''}{p.state ? ` ${p.state}` : ''}{p.zip ? ` ${p.zip}` : ''}</div>
                <div style={{ fontSize: 12.5, color: 'var(--slate)', marginTop: 4 }}>
                  {[p.label, p.externalId, (p.units || []).filter(u => u.isActive !== false).length ? `${(p.units || []).filter(u => u.isActive !== false).length} units` : ''].filter(Boolean).join(' · ')}
                </div>
                {(p.access?.gateCode || p.access?.lockboxCode) && (
                  <div style={{ fontSize: 12.5, color: 'var(--slate)', marginTop: 4 }}>
                    {p.access.gateCode ? `Gate ${p.access.gateCode}` : ''}
                    {p.access.gateCode && p.access.lockboxCode ? ' · ' : ''}
                    {p.access.lockboxCode ? `Lockbox ${p.access.lockboxCode}` : ''}
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })()}

      {/* ── One property's own page ──────────────────────────────────────
          What has happened at this building, and where the next job starts.

          Most commercial work never arrives through the portal: Julian walks a
          turn with the manager and quotes it standing in the unit. So the two
          buttons here make a job and open the sheet for it in one motion — an
          estimate when there is a price to agree, an invoice when the work is
          done and there was never anything to agree. */}
      {adminView === 'accounts' && adminAccountDetail && adminPropertyId && (() => {
        const d = adminAccountDetail;
        const p = (d.properties || []).find(x => String(x._id) === String(adminPropertyId));
        // The property was removed, or the account was re-read and it is gone.
        // Falling back to the account beats a blank screen with no way out.
        if (!p) return (
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setAdminPropertyId(null)}>← {d.account.name}</button>
            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--muted)' }}>That property is no longer on this account.</div>
          </div>
        );

        const units = (p.units || []).filter(u => u.isActive !== false);
        // Every job ever raised at this building. The account detail already
        // carries them, so this is a filter rather than a second fetch.
        const jobs = (d.requests || []).filter(r => String(r.propertyId) === String(p._id));
        const where = [p.city, p.state].filter(Boolean).join(', ');

        return (
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
            <button className="btn btn-ghost btn-sm" style={{ marginBottom: 12 }} onClick={() => setAdminPropertyId(null)}>← {d.account.name}</button>
            <div className="section-title">{p.addressLine1}{p.addressLine2 ? ` ${p.addressLine2}` : ''}</div>
            <div style={{ fontSize: 13, color: 'var(--slate)', marginBottom: 12 }}>
              {[where, p.zip].filter(Boolean).join(' ')}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
              {p.label && <span className="tag tag-slate">{p.label}</span>}
              {p.externalId && <span className="tag tag-slate">{p.externalId}</span>}
              <span className="tag tag-purple">{p.propertyType}</span>
              {units.length > 0 && <span className="tag tag-blue">{units.length} units</span>}
              {p.nteAmount != null && <span className="tag tag-amber">Limit {fmtMoney(p.nteAmount)}</span>}
            </div>

            {/* The codes, on the page rather than behind an edit form: this is
                what gets read down the phone to a tech sitting outside a gate. */}
            {(p.access?.gateCode || p.access?.lockboxCode || p.access?.pets || p.access?.parkingNotes) && (
              <div className="lead-card" style={{ cursor: 'default' }}>
                <div style={{ fontSize: 12.5, color: 'var(--slate)', lineHeight: 1.7 }}>
                  {p.access.gateCode && <div><strong>Gate</strong> {p.access.gateCode}</div>}
                  {p.access.lockboxCode && <div><strong>Lockbox</strong> {p.access.lockboxCode}</div>}
                  {p.access.pets && <div><strong>Pets</strong> {p.access.pets}</div>}
                  {p.access.parkingNotes && <div><strong>Parking</strong> {p.access.parkingNotes}</div>}
                </div>
              </div>
            )}

            {units.length > 0 && (
              <>
                <div className="sec-label" style={{ marginTop: 18 }}>Units ({units.length})</div>
                {units.map(u => (
                  <div key={u._id} className="lead-card" style={{ cursor: 'default' }}>
                    <div className="lead-job">#{u.unitNumber}</div>
                    <div style={{ fontSize: 12.5, color: 'var(--slate)', marginTop: 4 }}>
                      {[u.occupancy, u.tenantName, u.tenantPhone].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* Files on the building: photos and video of what needs doing,
                the inspection PDF, receipts for anything bought for it. */}
            <div style={{ marginTop: 20 }}>
              <DocumentList
                title="Photos, video & files"
                documents={p.documents || []}
                units={units}
                onAdd={addAdminPropertyDocuments}
                onRemove={removeAdminPropertyDocument}
                emptyText="Nothing filed against this property yet. Photos, video, inspections, receipts — anything that belongs to the building rather than to one job."
              />
            </div>

            <button className="btn btn-primary" style={{ width: '100%', margin: '18px 0 8px' }}
              onClick={() => openPropertyJobForm(d.account, p, 'request')}>+ New service request</button>
            <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
              <button className="btn btn-outline btn-sm" style={{ flex: 1 }}
                onClick={() => openPropertyJobForm(d.account, p, 'estimate')}>+ New estimate</button>
              <button className="btn btn-outline btn-sm" style={{ flex: 1 }}
                onClick={() => openPropertyJobForm(d.account, p, 'invoice')}>+ New invoice</button>
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 14, lineHeight: 1.5 }}>
              All three start a job at this property — the address, the codes and the spending limit come off
              this record, so all you type is what the work is. A service request just logs it; the other two
              go straight on to the price or the bill.
            </div>

            <div className="sec-label">Jobs here ({jobs.length})</div>
            {jobs.length === 0 && (
              <div className="lead-card" style={{ cursor: 'default' }}>
                <div style={{ fontSize: 13, color: 'var(--slate)', lineHeight: 1.5 }}>
                  Nothing has been quoted or billed at this property yet.
                </div>
              </div>
            )}
            {jobs.map(r => {
              const hasEstimate = r.estimate?.amount != null;
              const hasInvoice = r.invoice?.amount != null;
              return (
                <div key={r._id} className="lead-card" style={{ cursor: 'default', position: 'relative' }}>
                  {/* Top right, where a card's own delete belongs — and where
                      it is the same distance from every job on the page,
                      whether it was logged this morning or last spring. */}
                  <button
                    className="card-x"
                    aria-label={`Delete this service request — ${r.serviceType}`}
                    title="Delete this service request"
                    onClick={() => setConfirmDelete({
                      type: 'service request',
                      id: r._id,
                      label: [r.workOrder?.number, r.serviceType, r.unitLabel].filter(Boolean).join(' · '),
                    })}
                  >×</button>
                  <div className="lead-job" style={{ paddingRight: 28 }}>
                    {r.serviceType}{r.unitLabel ? ` · ${r.unitLabel}` : ''}
                    <span style={{ marginLeft: 8 }}>{statusTag(r.status)}</span>
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--slate)', margin: '4px 0 6px', lineHeight: 1.45 }}>
                    {r.workOrder?.number ? <strong>{r.workOrder.number} · </strong> : null}
                    {r.description}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {hasEstimate && (
                      <span className={`tag tag-${r.estimateStatus === 'approved' ? 'green' : 'blue'}`}>
                        Estimate {fmtMoney(r.estimate.amount)}
                      </span>
                    )}
                    {hasInvoice && (
                      <span className={`tag tag-${r.invoiceStatus === 'paid' ? 'green' : 'amber'}`}>
                        Invoice {fmtMoney(r.invoice.amount)}
                      </span>
                    )}
                    {r.nteStatus === 'exceeded' && <span className="tag tag-red">Over the limit</span>}
                  </div>
                  {/* Convert, not copy: the agreed price becomes the bill line
                      for line, and stays editable — the job that got done is
                      never exactly the job that got quoted. */}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                    <button className="btn btn-outline btn-sm" onClick={() => openEstimate(r)}>
                      {hasEstimate ? 'Estimate' : 'Write estimate'}
                    </button>
                    {hasEstimate && !hasInvoice && (
                      <button className="btn btn-primary btn-sm" onClick={() => openAdminInvoice(r, 'estimate')}>Convert to invoice</button>
                    )}
                    <button className="btn btn-outline btn-sm" onClick={() => openAdminInvoice(r)}>
                      {hasInvoice ? 'Invoice' : 'Write invoice'}
                    </button>
                    {/* The sheet the job is done from. Raising it and opening
                        it are one motion — whether it already exists is our
                        problem, not his — and it carries no prices. */}
                    <button className="btn btn-outline btn-sm" disabled={isRaisingWorkOrder} onClick={() => openWorkOrder(r)}>
                      {r.workOrder?.number || 'Create work order'}
                    </button>
                    {/* Raised on the wrong job, raised twice, or the job moved
                        on far enough that the sheet in the van is now wrong —
                        this takes it back without touching the job itself. */}
                    {r.workOrder?.number && (
                      <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)', borderColor: 'var(--red)' }}
                        onClick={() => setConfirmDelete({ type: 'work order', id: r._id, label: r.workOrder.number })}>
                        Delete WO
                      </button>
                    )}
                    <button className="btn btn-green btn-sm"
                      onClick={() => { setActiveRequest(r); setAssignVendorId(String(r.assignedVendorId?._id || r.assignedVendorId || r.requestedVendorId || '')); setModal('assign'); }}>
                      {r.assignedVendorId ? 'Reassign' : 'Assign technician'}
                    </button>
                  </div>

                  {/* Photos, video, receipts and PDFs against this job. The one
                      place an expense on a turn can actually be filed. */}
                  <div style={{ marginTop: 12 }}>
                    <DocumentList
                      documents={r.documents || []}
                      onAdd={(files, meta) => addJobDocuments(r, files, meta)}
                      emptyText="No photos, video or receipts on this job yet."
                    />
                  </div>
                  <div className="lead-footer">
                    <span className="lead-date">{fmtDate(r.createdAt)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* A client's own page — the Joist Profile screen. It takes over the
          Clients tab the way a vendor's page takes over Vendors. */}
      {adminView === 'clients' && activeClient && (() => {
        // Re-read from `clients` so an edit made on the page shows on it.
        const c = clients.find(x => x._id === activeClient._id) || activeClient;
        return (
          <ClientProfile
            client={c}
            projects={projects}
            requests={requests}
            onBack={() => setActiveClient(null)}
            onEdit={openClientForm}
            onAddLead={openLeadForClient}
            onOpenEstimate={openEstimate}
            onOpenProject={(project) => { setActiveClient(null); setAdminView('projects'); setActiveProject(project); }}
            onCreateEstimate={openEstimateForClient}
            onCreateInvoice={openClientInvoice}
            onInvoiceEstimate={openInvoiceFromEstimate}
            onPrintInvoice={openInvoiceDoc}
            onDeleteEstimate={askDeleteEstimate}
            onDeleteInvoice={askDeleteInvoice}
          />
        );
      })()}

      {adminView === 'clients' && !activeClient && (
      <>
      {/* Same place, and the same reasoning, as + Add Lead: with the list it
          adds to, and outside the scroll so it survives a long client list. */}
      <div style={{ background: 'white', borderBottom: '1px solid var(--border)', padding: '10px 20px' }}>
        <button className="btn btn-primary btn-sm" onClick={openAddClientForm}>+ Add Client</button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {clients.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--muted)' }}>No clients yet. They land here when they create an account — or add one yourself with + Add Client.</div>
          ) : (
            clients.map(c => {
              // Where the work is. The billing address is only worth a line
              // when it is somewhere else — an owner who lives out of state,
              // a rental billed to the landlord.
              const where = [c.address, c.address2, c.city, c.state, c.zip].filter(Boolean).join(', ');
              const billTo = [c.billingAddress, c.billingAddress2, c.billingCity, c.billingState, c.billingZip]
                .filter(Boolean).join(', ');
              return (
              <SwipeToDelete key={c._id} onDelete={() => setConfirmDelete({ type: 'client', id: c._id, label: c.name })}>
              {/* The whole card opens their page — everything billed, everything
                  owed, every estimate — which is what a name in a client list is
                  for. Edit stays on the card because correcting a phone number
                  taken down wrong shouldn't cost a screen. */}
              <div className="lead-card card-tappable" role="button" tabIndex={0}
                onClick={() => setActiveClient(c)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveClient(c); } }}>
                <div className="lead-name">{c.name}</div>
                <div style={{ fontSize: 12, color: 'var(--slate)', margin: '4px 0' }}>
                  {c.phone
                    ? <a href={`tel:${c.phone}`} onClick={e => e.stopPropagation()} style={{ color: 'var(--blue)', textDecoration: 'none' }}>{c.phone}</a>
                    : <span style={{ color: 'var(--amber)' }}>No phone given</span>}
                  {c.phone2 && <> · <a href={`tel:${c.phone2}`} onClick={e => e.stopPropagation()} style={{ color: 'var(--blue)', textDecoration: 'none' }}>{c.phone2}</a></>}
                  {c.email && <> · <a href={`mailto:${c.email}`} onClick={e => e.stopPropagation()} style={{ color: 'var(--blue)', textDecoration: 'none' }}>{c.email}</a></>}
                </div>
                {where && <div style={{ fontSize: 13, color: 'var(--slate)', margin: '2px 0' }}>{where}</div>}
                {billTo && (
                  <div style={{ fontSize: 12, color: 'var(--muted)', margin: '2px 0' }}>Bill to: {billTo}</div>
                )}
                {c.notes && (
                  <div style={{ fontSize: 12.5, color: 'var(--slate)', margin: '6px 0 0', lineHeight: 1.4 }}>{c.notes}</div>
                )}
                <div className="lead-footer">
                  <span className="tag tag-slate">Client</span>
                  <span className="lead-date">Joined {fmtDate(c.createdAt)}</span>
                </div>
                {/* A typo in a phone number used to mean deleting the client
                    and starting again. Putting an email on somebody who had
                    none is the same button, and hands you their portal link. */}
                <div style={{ marginTop: 10 }}>
                  <button className="btn btn-outline btn-sm"
                    onClick={e => { e.stopPropagation(); openClientForm(c); }}>Edit</button>
                </div>
              </div>
              </SwipeToDelete>
              );
            })
          )}
      </div>
      </>
      )}
    </div>
  );
}
